import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ArrowRightLeft, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { StockItem, LocationQuantity } from "@/types/stock-item";
import { mockWarehouses, mockLocations } from "@/data/mockWarehousesLocations";
import { useWarehousesLocations } from "@/hooks/use-warehouses-locations";
import { useStockMovements } from "@/hooks/use-stock-movements";

interface QuickTransferModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: StockItem;
  isLiveMode?: boolean;
  onTransferComplete?: () => void;
}

export function QuickTransferModal({
  open,
  onOpenChange,
  item,
  isLiveMode = true,
  onTransferComplete,
}: QuickTransferModalProps) {
  // Backend data hook
  const {
    warehouses: backendWarehouses,
    locations: backendLocations,
    fetchWarehouses,
    fetchLocations,
  } = useWarehousesLocations();

  const { createMovement } = useStockMovements();

  // Get data source based on mode
  const warehouses = useMemo(() => {
    return isLiveMode ? backendWarehouses : mockWarehouses;
  }, [isLiveMode, backendWarehouses]);

  const allLocations = useMemo(() => {
    return isLiveMode ? backendLocations : mockLocations;
  }, [isLiveMode, backendLocations]);

  // Get location quantities
  const locationQuantities = useMemo(() => {
    if (item.locationQuantities && item.locationQuantities.length > 0) {
      return item.locationQuantities;
    }
    // Initialize with current warehouse/location if available
    if (item.warehouseCode && item.locationCode && item.physicalQuantity > 0) {
      return [
        {
          warehouseCode: item.warehouseCode,
          locationCode: item.locationCode,
          quantity: item.physicalQuantity,
        },
      ];
    }
    return [];
  }, [item]);

  // Check if single or multiple locations
  const isSingleLocation = locationQuantities.length === 1;

  // Form state
  const [sourceWarehouse, setSourceWarehouse] = useState("");
  const [sourceLocation, setSourceLocation] = useState("");
  const [toWarehouse, setToWarehouse] = useState("");
  const [toLocation, setToLocation] = useState("");
  const [quantity, setQuantity] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Fetch warehouses and locations when in live mode
  useEffect(() => {
    if (isLiveMode && open) {
      fetchWarehouses();
    }
  }, [isLiveMode, open, fetchWarehouses]);

  // Initialize form when dialog opens
  useEffect(() => {
    if (open && locationQuantities.length > 0) {
      // Auto-populate source warehouse and location
      const firstLocation = locationQuantities[0];
      setSourceWarehouse(firstLocation.warehouseCode);
      setSourceLocation(firstLocation.locationCode);
      
      // Fetch locations for source warehouse
      if (isLiveMode) {
        fetchLocations({ warehouseCode: firstLocation.warehouseCode });
      }
    }
  }, [open, locationQuantities, isLiveMode, fetchLocations]);

  // Fetch locations when warehouse changes
  useEffect(() => {
    if (isLiveMode && toWarehouse) {
      fetchLocations({ warehouseCode: toWarehouse });
    }
  }, [isLiveMode, toWarehouse, fetchLocations]);

  // Get available source locations (where stock exists)
  const sourceLocations = useMemo(() => {
    return locationQuantities.filter(
      (loc) => loc.warehouseCode === sourceWarehouse
    );
  }, [locationQuantities, sourceWarehouse]);

  // Get available source warehouses (where stock exists)
  const sourceWarehouses = useMemo(() => {
    const warehouseCodes = [...new Set(locationQuantities.map((loc) => loc.warehouseCode))];
    return warehouses.filter((w) => warehouseCodes.includes(w.code) && w.active);
  }, [locationQuantities, warehouses]);

  // Get available destination locations for selected warehouse
  const destinationLocations = useMemo(() => {
    if (!toWarehouse) return [];
    return allLocations.filter(
      (loc) => loc.warehouseCode === toWarehouse && loc.active
    );
  }, [allLocations, toWarehouse]);

  // Get available quantity for selected source location
  const availableQuantity = useMemo(() => {
    const location = locationQuantities.find(
      (loc) =>
        loc.warehouseCode === sourceWarehouse &&
        loc.locationCode === sourceLocation
    );
    return location?.quantity || 0;
  }, [locationQuantities, sourceWarehouse, sourceLocation]);

  // Get warehouse name
  const getWarehouseName = (code: string) => {
    const warehouse = warehouses.find((w) => w.code === code);
    return warehouse?.name || code;
  };

  // Reset form
  const resetForm = () => {
    setSourceWarehouse("");
    setSourceLocation("");
    setToWarehouse("");
    setToLocation("");
    setQuantity("");
    setShowConfirmation(false);
  };

  // Handle dialog close
  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  // Validate form
  const validateForm = (): boolean => {
    if (!sourceWarehouse || !sourceLocation) {
      toast.error("Please select a source warehouse and location");
      return false;
    }

    if (!toWarehouse || !toLocation) {
      toast.error("Please select a destination warehouse and location");
      return false;
    }

    if (sourceWarehouse === toWarehouse && sourceLocation === toLocation) {
      toast.error("Source and destination cannot be the same");
      return false;
    }

    const qty = parseFloat(quantity);
    if (!quantity || isNaN(qty) || qty <= 0) {
      toast.error("Please enter a valid quantity");
      return false;
    }

    if (qty > availableQuantity) {
      toast.error(`Quantity cannot exceed available stock (${availableQuantity})`);
      return false;
    }

    return true;
  };

  // Handle transfer submission
  const handleTransfer = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Create stock movement record
      const movementData = {
        stockCode: item.code,
        stockDescription: item.description,
        movementType: "Transfer",
        movementSource: "Manual",
        locationCode: sourceLocation,
        quantity: -parseFloat(quantity), // Negative for outgoing
        unitPrice: item.lastCostPrice || item.unitPrice,
        notes: `Transfer from ${sourceWarehouse}/${sourceLocation} to ${toWarehouse}/${toLocation}`,
        userName: "Current User", // This should come from auth context
      };

      // In live mode, create the movement via API
      if (isLiveMode) {
        await createMovement(movementData);
      }

      // Show confirmation
      setShowConfirmation(true);

      // Call completion callback
      setTimeout(() => {
        onTransferComplete?.();
        handleClose();
        toast.success("Stock transfer completed successfully");
      }, 1500);
    } catch (error) {
      console.error("Transfer error:", error);
      toast.error("Failed to complete transfer. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            Quick Transfer
          </DialogTitle>
          <DialogDescription>
            Transfer stock between warehouses and locations
          </DialogDescription>
        </DialogHeader>

        {showConfirmation ? (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="rounded-full bg-green-100 p-3">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold">Transfer Completed!</h3>
              <p className="text-sm text-muted-foreground">
                Stock has been transferred successfully
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Stock Item Info */}
            <div className="p-4 bg-muted/50 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Stock Item</p>
                  <p className="font-semibold">{item.code}</p>
                  <p className="text-sm">{item.description}</p>
                </div>
                <Badge variant="secondary">
                  Total: {item.physicalQuantity} {item.unitOfMeasure}
                </Badge>
              </div>
            </div>

            {/* Source Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="h-px flex-1 bg-border" />
                <span className="text-sm font-medium text-muted-foreground">FROM</span>
                <div className="h-px flex-1 bg-border" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="source-warehouse">Source Warehouse</Label>
                  {isSingleLocation ? (
                    <Input
                      id="source-warehouse"
                      value={getWarehouseName(sourceWarehouse)}
                      readOnly
                      className="bg-muted"
                    />
                  ) : (
                    <Select
                      value={sourceWarehouse}
                      onValueChange={(value) => {
                        setSourceWarehouse(value);
                        setSourceLocation(""); // Reset location when warehouse changes
                      }}
                    >
                      <SelectTrigger id="source-warehouse">
                        <SelectValue placeholder="Select warehouse" />
                      </SelectTrigger>
                      <SelectContent>
                        {sourceWarehouses.map((warehouse) => (
                          <SelectItem key={warehouse.code} value={warehouse.code}>
                            {warehouse.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="source-location">Source Location</Label>
                  {isSingleLocation ? (
                    <Input
                      id="source-location"
                      value={sourceLocation}
                      readOnly
                      className="bg-muted font-mono"
                    />
                  ) : (
                    <Select
                      value={sourceLocation}
                      onValueChange={setSourceLocation}
                      disabled={!sourceWarehouse}
                    >
                      <SelectTrigger id="source-location">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {sourceLocations.map((loc) => (
                          <SelectItem key={loc.locationCode} value={loc.locationCode}>
                            <div className="flex flex-col">
                              <span className="font-mono">{loc.locationCode}</span>
                              <span className="text-xs text-muted-foreground">
                                Available: {loc.quantity}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              {sourceLocation && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Available quantity at this location: <strong>{availableQuantity}</strong>{" "}
                    {item.unitOfMeasure}
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* Destination Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="h-px flex-1 bg-border" />
                <span className="text-sm font-medium text-muted-foreground">TO</span>
                <div className="h-px flex-1 bg-border" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="to-warehouse">Destination Warehouse</Label>
                  <Select value={toWarehouse} onValueChange={setToWarehouse}>
                    <SelectTrigger id="to-warehouse">
                      <SelectValue placeholder="Select warehouse" />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouses
                        .filter((w) => w.active)
                        .map((warehouse) => (
                          <SelectItem key={warehouse.code} value={warehouse.code}>
                            {warehouse.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="to-location">Destination Location</Label>
                  <Select
                    value={toLocation}
                    onValueChange={setToLocation}
                    disabled={!toWarehouse}
                  >
                    <SelectTrigger id="to-location">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {destinationLocations.map((location) => (
                        <SelectItem key={location.locationCode} value={location.locationCode}>
                          <div className="flex flex-col">
                            <span className="font-mono">{location.locationCode}</span>
                            <span className="text-xs text-muted-foreground">
                              {location.locationType}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Quantity Section */}
            <div className="space-y-2">
              <Label htmlFor="quantity">
                Quantity to Transfer <span className="text-destructive">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  id="quantity"
                  type="number"
                  min="0"
                  max={availableQuantity}
                  step="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="Enter quantity"
                  className="flex-1"
                />
                <div className="flex items-center px-3 border rounded-md bg-muted">
                  <span className="text-sm font-medium">{item.unitOfMeasure}</span>
                </div>
              </div>
              {quantity && parseFloat(quantity) > availableQuantity && (
                <p className="text-sm text-destructive">
                  Quantity exceeds available stock
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {!showConfirmation && (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button onClick={handleTransfer} disabled={isSubmitting}>
                {isSubmitting ? "Processing..." : "Confirm Transfer"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
